import { FunctionComponent as FC, useEffect, useState } from 'react';
import { withStyles, Card, DropdownMenu, Icon, MaterialSymbol } from 'vopay';
import type { ClientAccountBalance, SupportedCurrencies } from 'vopay';
import styles from './PaymentMethodsTab.module.scss';
import usePaymentMethods, { PaymentMethod } from '@/hooks/usePaymentMethods';
import useFetchPaylinkSettings from '@/hooks/useApi/useFetchElinxSettings';
import type { FormValues as AddInteracFormValues } from '@/components/Customer/AddInteracModal';
import PaymentMethodDetails from './PaymentMethodDetails';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import AddMethodMenu from './AddMethodMenu';
import ProvidePaymentMethod from '@/components/Customer/ProvidePaymentMethod/ProvidePaymentMethod';
import ElinxRequestDialog from './ElinxRequestDialog';
import PaymentMethodListItem from './PaymentMethodListItem/PaymentMethodListItem';
import { useDialog } from '@/providers/Dialog.provider';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI';
import { useDataFetchIndicator } from '@/hooks';

const cn = withStyles(styles);

type ComponentProps = {
  accountID: string;
  accountName: string;
  email: string;
  balances?: ClientAccountBalance[];
  active: boolean;
};

const PaymentMethodsTab: FC<ComponentProps> = ({ accountID, accountName, email, balances, active }) => {
  const { response: loginUser } = useClientData();
  const loginUserName = loginUser.LoginUserName;
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const [, dataFetchIndicator] = useDataFetchIndicator();

  // TODO: error handling for fetchMethods
  const { groupedMethods, fetchMethods, setDefaultMethod, deleteMethod, deleteGroupedMethod } = usePaymentMethods({
    accountID: accountID,
    currencies: balances ? Object.values(balances).map(({ Currency }) => Currency as SupportedCurrencies) : ['CAD'],
  });

  const [showingAddMethod, setShowingAddMethod] = useState(false);
  const [paylinkSetting, fetchPaylinkSetting] = useFetchPaylinkSettings({});
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [addPaymentDropdownExpanded, setAddPaymentDropdownExpanded] = useState(false);
  const dialog = useDialog();

  useEffect(() => {
    fetchPaylinkSetting({
      PortalUser: loginUserName,
    });

    fetchMethods();
  }, []);

  useEffect(() => {
    setSelectedMethod(groupedMethods.find((m) => m.token === selectedMethod?.token) || groupedMethods[0]);
  }, [groupedMethods]);

  async function handleSetDefault(method: PaymentMethod) {
    const { type, token, details, typeLabel } = method;
    try {
      dataFetchIndicator(true);

      const res = await setDefaultMethod(type, token);

      if (res.Success) {
        await fetchMethods();
        toast.success(`${details} set as default ${type} for ${typeLabel}`);
      }
    } finally {
      dataFetchIndicator(false);
    }
  }

  const handleMethodAdded = async (type?: string) => {
    await fetchMethods();
    setShowingAddMethod(false);

    if (type) {
      toast.success(
        t('Toast.AddMethod.added', {
          defaultValue: type + ' added',
          type,
        }),
      );
    }
  };

  const handleBankAdded = () => {
    handleMethodAdded(t('Toast.AddMethod.bankAccount', { defaultValue: 'Bank account' }));
  };

  const handleCreditCardAdded = async (token: string) => {
    const alreadyHasCreditCard = groupedMethods.some((m) => m.type === 'CreditCard');

    if (!alreadyHasCreditCard) {
      const res = await setDefaultMethod('CreditCard', token);

      if (res.ErrorMessage) {
        await alertDialog.open(
          t('Dialog.AddMethod.assignDefault', {
            defaultValue: 'Payment method has been successfully added. Please assign a default payment method',
          }),
          null,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );
      }
    }

    handleMethodAdded(t('Toast.AddMethod.creditCard', { defaultValue: 'Credit card' }));
  };

  const handleDebitCardAdded = async (token: string) => {
    const alreadyHasDebitCard = groupedMethods.some((m) => m.type === 'DebitCard');

    if (!alreadyHasDebitCard) {
      const res = await setDefaultMethod('DebitCard', token);

      if (res.ErrorMessage) {
        await alertDialog.open(
          t('Dialog.AddMethod.assignDefault', {
            defaultValue: 'Payment method has been successfully added. Please assign a default payment method',
          }),
          null,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );
      }
    }
    handleMethodAdded(t('Toast.AddMethod.debitCard', { defaultValue: 'Debit card' }));
  };

  const handleAddInterac = async (token: string, eTransferMethod: AddInteracFormValues['eTransferMethod']) => {
    const hasAtLeastOneInterac = groupedMethods.some((m) => ['Email', 'PhoneNumber'].includes(m.type));

    if (!hasAtLeastOneInterac) {
      const setDefaultResponse = await setDefaultMethod('Email', token);

      if (!setDefaultResponse.Success) {
        console.error(setDefaultResponse.ErrorMessage);

        await alertDialog.open(
          t('Dialog.AddMethod.assignDefault', {
            defaultValue: 'Payment method has been successfully added. Please assign a default payment method',
          }),
          null,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );

        handleMethodAdded();

        return;
      }
    }

    if (eTransferMethod === 'Email') {
      handleMethodAdded(
        t('Toast.AddMethod.email', {
          defaultValue: 'Email',
        }),
      );
    } else {
      handleMethodAdded(
        t('Toast.AddMethod.phone', {
          defaultValue: 'Phone number',
        }),
      );
    }
  };

  const handleDeletePaymentMethod = async (method: PaymentMethod) => {
    const contextTypeLabel =
      method.kind === 'PHONE'
        ? 'Phone Number'
        : method.kind === 'EMAIL'
          ? 'Email Address'
          : ['GooglePay', 'ApplePay'].includes(method.type)
            ? 'Credit Card'
            : method.typeLabel;

    const decision = await alertDialog.caution(
      t('Dialog.Delete.title', {
        typeLabel: contextTypeLabel,
        context: method.type,
      }),
      t('Dialog.Delete.content', {
        typeLabel: contextTypeLabel,
        context: method.type,
      }),
      {
        abortLabel: tGlobal('Buttons.cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Dialog.Delete.confirmLabel', { defaultValue: 'Delete' }),
      },
    );

    if (decision !== 'confirm') return;

    const res = await deleteGroupedMethod(method);
    const hasError = res.some((r) => !r.Success);

    if (hasError) {
      alertDialog.error(
        t('Toast.Delete.error', {
          defaultValue: 'Something went wrong trying to delete your account.',
        }),
        null,
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );
    } else {
      toast.neutral(
        t('Toast.Delete.success', {
          defaultValue: `${method.details} ${method.type} has been deleted.`,
          details: method.details,
          type: method.type,
        }),
      );
    }

    fetchMethods();
  };

  const handleDeletePaymentRail = async (method: PaymentMethod) => {
    const decision = await alertDialog.caution(
      t('Dialog.DeleteRail.title', {
        defaultValue: `Delete ${method.typeLabel}`,
        typeLabel: method.typeLabel,
      }),
      t('Dialog.DeleteRail.content', {
        defaultValue: `This action will delete the ${method.typeLabel} information associated with ${method.details}.`,
        typeLabel: method.typeLabel,
        details: method.details,
      }),
      {
        abortLabel: tGlobal('Buttons.cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Dialog.Delete.confirmLabel', { defaultValue: 'Delete' }),
      },
    );

    if (decision !== 'confirm') return;

    const res = await deleteMethod(method);

    if (res.Success) {
      await fetchMethods();
      toast.neutral(
        t('Toast.Delete.success', {
          defaultValue: `${method.details} ${method.type} has been deleted.`,
          details: method.details,
          type: method.type,
        }),
      );
    } else {
      console.error(res.ErrorMessage);

      alertDialog.error(
        t('Toast.Delete.error', {
          defaultValue: 'Something went wrong trying to delete your account.',
        }),
        null,
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );
    }
  };

  async function handleRequest() {
    await dialog.open(({ onCancel }) => (
      <ElinxRequestDialog accountName={accountName} accountID={accountID} email={email} onClose={onCancel} />
    ));
  }

  return (
    <>
      {groupedMethods.length > 0 && (
        <div className={cn('paymentMethodsContainer')}>
          <section className={cn('left')}>
            <header className={cn('listHeader')}>
              {t('tabTitle', { defaultValue: 'Payment Methods' })}

              <AddMethodMenu
                disabled={!active}
                onRequestClick={handleRequest}
                onProvideClick={() => setShowingAddMethod(true)}
              />
            </header>

            <div className={cn('methodsContainer')}>
              <ul data-testid="paymentMethods-list" className={cn('methods', 'scrollbar')}>
                {groupedMethods.map((m, i) => (
                  // TODO: remove this m.token + i line when duplicated token data is fixed
                  <li data-testid={`paymentMethod-${i}`} key={m.token + i} onClick={() => setSelectedMethod(m)}>
                    <PaymentMethodListItem
                      method={m}
                      selected={selectedMethod?.token === m.token}
                      onSetDefault={handleSetDefault}
                      active={active}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {selectedMethod && (
            <section className={cn('right')} aria-label={`details for ${selectedMethod?.details}`}>
              <Card
                className={cn('detailsCard')}
                style={{
                  padding: '1px', // fix bottom border quirk when scrolling
                  shadowOpacity: 0.12,
                  dropShadow: '0px 10px 12px #2D2D2D1A',
                }}
              >
                <div className={cn('detailsContainer', 'scrollbar')}>
                  {paylinkSetting && selectedMethod && (
                    <PaymentMethodDetails
                      accountID={accountID}
                      method={selectedMethod}
                      email={email}
                      defaultCurrency={balances ? (balances[0].Currency as SupportedCurrencies) : 'CAD'}
                      onDeleteRail={handleDeletePaymentRail}
                      onDeleteMethod={handleDeletePaymentMethod}
                      onVerifyBank={fetchMethods}
                      onSetDefault={handleSetDefault}
                      active={active}
                    />
                  )}
                </div>
              </Card>
            </section>
          )}
        </div>
      )}

      {groupedMethods.length === 0 && (
        <div className={cn('emptyState')}>
          <Icon icon="bank3" color="grey" height="67px" width="67px" />
          <span className={cn('emptyStateContent')}>
            <h4 className={cn('emptyStateTitle')}>No Payment Methods Added</h4>
            <p className={cn('emptyStateDescription')}>
              You haven't linked a payment method to this contact yet. Add one to get started.
            </p>
          </span>
          <div>
            <DropdownMenu onOpenChange={setAddPaymentDropdownExpanded}>
              <DropdownMenu.Trigger asChild>
                <Button
                  iconRightProps={{
                    icon: addPaymentDropdownExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down',
                    fillAmount: 1,
                  }}
                  colorScheme="primary"
                  onClick={() => {
                    setAddPaymentDropdownExpanded(!addPaymentDropdownExpanded);
                  }}
                >
                  Add Payment Method
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  key="request"
                  onSelect={handleRequest}
                  left={<MaterialSymbol icon="call_received" size="18px" />}
                >
                  Request
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  key="provide"
                  onSelect={() => setShowingAddMethod(true)}
                  left={<MaterialSymbol icon="article" fillAmount={1} size="18px" />}
                >
                  Provide
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>
        </div>
      )}

      <ProvidePaymentMethod
        isOpen={showingAddMethod}
        accountName={accountName}
        accountID={accountID}
        onClose={() => setShowingAddMethod(false)}
        onAddInterac={handleAddInterac}
        onBankAdded={handleBankAdded}
        onCreditCardAdded={handleCreditCardAdded}
        onDebitCardAdded={handleDebitCardAdded}
      />
    </>
  );
};

export default PaymentMethodsTab;
